import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
} from "react";
import {
  User,
  onAuthStateChanged,
  signOut,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { auth, db } from "../configs/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { Flex, Spinner } from "@chakra-ui/react";
import GlobalSpinner from "../components/CustomReusables/GlobalSpinner";

export interface UserAddress {
  id: string;
  label: string;
  addressLine: string;
  latitude: number;
  longitude: number;
  instructions?: string;
  isCurrent: boolean;
  createdAt: number;
  isPickup?: boolean;
}

export interface UserSettings {
  id?: string;
  name?: string;
  displayName?: string;
  email?: string;
  phone?: string;
  phoneNumber?: string;
  role?: string;
  theme?: string;
  fontColor?: string;
  businessName?: string;
  businessAddress?: string;
  logoURL?: string;
  addresses?: UserAddress[];
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  userSettings: UserSettings | null;
  currentAddress?: UserAddress;
  setCurrentAddress: (id: string) => Promise<void>;
  refreshUserSettings: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUserSettings = async () => {
    if (!user) return;

    const docRef = doc(db, "users", user.uid);
    const snap = await getDoc(docRef);

    if (snap.exists()) {
      setUserSettings(snap.data() as UserSettings);
    } else {
      setUserSettings(null);
    }
  };

  useEffect(() => {
    setPersistence(auth, browserLocalPersistence)
      .then(() =>
        onAuthStateChanged(auth, async (firebaseUser) => {
          setUser(firebaseUser);

          if (firebaseUser) {
            const docRef = doc(db, "users", firebaseUser.uid);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
              setUserSettings(docSnap.data() as UserSettings);
            } else {
              setUserSettings(null);
            }
          } else {
            setUserSettings(null);
          }

          setLoading(false);
        }),
      )
      .catch(() => setLoading(false));
  }, []);

  const currentAddress = useMemo(() => {
    return userSettings?.addresses?.find((a) => a.isCurrent);
  }, [userSettings]);

  const setCurrentAddress = async (id: string) => {
    if (!user || !userSettings?.addresses) return;

    const updated = userSettings.addresses.map((addr) => ({
      ...addr,
      isCurrent: addr.id === id,
    }));

    await updateDoc(doc(db, "users", user.uid), {
      addresses: updated,
    });

    setUserSettings((prev) => (prev ? { ...prev, addresses: updated } : prev));
  };

  const logout = async () => {
    await signOut(auth);
  };

  if (loading) {
    return (
      <Flex
        height="100vh"
        width="100vw"
        alignItems="center"
        justifyContent="center"
        bg="white"
      >
        <GlobalSpinner fullPage />
      </Flex>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        userSettings,
        currentAddress,
        setCurrentAddress,
        refreshUserSettings,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
